// frontend/src/components/staff/WeeklyHourLimitModal.js
//
// Workforce — SCHED-SLICE1-E weekly-hour-cap WARN-WITH-CONFIRM modal.
//
// Q2 lock (workforce-scheduling.md §"Roderick decisions log"): the cap
// is warn-with-confirm — admin can override via an explicit confirmation.
// Visual spec §10.2 + story Scenario 5.
//
// Triggered when the BE response (POST/PUT /api/workforce/shifts) returns
// a non-null `conflicts.weeklyHourLimit` descriptor. The optional reason
// textarea is captured and logged via the BE per Story C
// `metadata.weeklyLimitOverrideReason` (Slice 1 reads the field through
// from FE-state; Story C BE persists it).
//
// Confirm button label is verbatim "Confirm and assign anyway" per
// Roderick's directive — copy carries the friction, NOT a red button.
//
// Read-only props by design — caller owns open/close + decision routing.

import React from "react";
import "./WeeklyHourLimitModal.css";

const WeeklyHourLimitModal = ({
  open,
  staffName,
  weeklyHourLimit,
  onConfirm,
  onCancel,
  isSubmitting,
}) => {
  const [reason, setReason] = React.useState("");

  // Esc closes (a11y).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && typeof onCancel === "function") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  // Reset textarea each time the modal opens with a fresh trigger.
  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);

  if (!open || !weeklyHourLimit) return null;

  const {
    currentWeekHours = 0,
    limit = 0,
    projectedHoursAfter = 0,
  } = weeklyHourLimit;
  const overBy = Math.max(0, Number(projectedHoursAfter) - Number(limit));
  const overByLabel = `${overBy.toFixed(1)} hour${overBy === 1 ? "" : "s"}`;
  const projLabel = `${Number(projectedHoursAfter).toFixed(1)} hours`;
  const limitLabel = `${Number(limit).toFixed(0)} hours`;
  const currentLabel = `${Number(currentWeekHours).toFixed(1)} hours`;
  const safeName = staffName || "This staff member";

  const handleConfirm = () => {
    if (typeof onConfirm === "function") {
      onConfirm({ reason: reason.trim() || null });
    }
  };

  return (
    <>
      <div
        className="weekly-hour-limit-modal__backdrop"
        onClick={onCancel}
        aria-hidden="true"
        data-testid="weekly-hour-limit-modal-backdrop"
      />
      <div
        className="weekly-hour-limit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="weekly-hour-limit-modal-heading"
        data-testid="weekly-hour-limit-modal"
      >
        <header className="weekly-hour-limit-modal__header">
          <h2
            id="weekly-hour-limit-modal-heading"
            className="weekly-hour-limit-modal__heading"
          >
            Confirm: {safeName} will exceed weekly hour limit
          </h2>
          <button
            type="button"
            className="weekly-hour-limit-modal__close"
            onClick={onCancel}
            aria-label="Close confirmation modal"
            data-testid="weekly-hour-limit-modal-close"
          >
            &times;
          </button>
        </header>

        <div className="weekly-hour-limit-modal__body">
          <p className="weekly-hour-limit-modal__lede">
            With this shift, {safeName} will be scheduled for {projLabel} this week.
          </p>
          <dl className="weekly-hour-limit-modal__stats">
            <div className="weekly-hour-limit-modal__stat">
              <dt>Currently scheduled</dt>
              <dd>{currentLabel}</dd>
            </div>
            <div className="weekly-hour-limit-modal__stat">
              <dt>Weekly limit</dt>
              <dd>{limitLabel}</dd>
            </div>
            <div className="weekly-hour-limit-modal__stat weekly-hour-limit-modal__stat--over">
              <dt>Over by</dt>
              <dd>{overByLabel}</dd>
            </div>
          </dl>
          <label
            htmlFor="weekly-hour-limit-modal-reason"
            className="weekly-hour-limit-modal__reason-label"
          >
            Reason for override (optional)
          </label>
          <textarea
            id="weekly-hour-limit-modal-reason"
            className="weekly-hour-limit-modal__reason-input"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
            data-testid="weekly-hour-limit-modal-reason"
            placeholder="e.g. covering a colleague's sick leave"
          />
        </div>

        <footer className="weekly-hour-limit-modal__footer">
          <button
            type="button"
            className="weekly-hour-limit-modal__cancel"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="weekly-hour-limit-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="weekly-hour-limit-modal__confirm"
            onClick={handleConfirm}
            disabled={isSubmitting}
            data-testid="weekly-hour-limit-modal-confirm"
          >
            {isSubmitting ? "Saving..." : "Confirm and assign anyway"}
          </button>
        </footer>
      </div>
    </>
  );
};

export default WeeklyHourLimitModal;
